import type {
  PayRunApprovalDto,
  PayRunApprovalRequirementDto,
  PayRunDetailDto,
  PayRunFanOutLegDto,
} from './payRunsApiTypes';
import type { PayRunExceptionsPage } from './payRunPopulationApiTypes';
import { readinessReviewDetail } from './payRunReadinessMessage';
import { resolvePayRunLifecycleStageIndex } from './payRunsModel';
import {
  bankingNeedsAttention,
  bankingStageDetail,
  bankingSubStepLabel,
  summarisePayRunBanking,
  type PayRunBankingInput,
  type PayRunBankingSummary,
} from './payRunBankingModel';

export type PayRunTimelineStepId =
  | 'pay_period'
  | 'review'
  | 'ready_to_pay'
  | 'approval'
  | 'payment'
  | 'close';

export type PayRunTimelineStepState =
  | 'complete'
  | 'current'
  | 'in_progress'
  | 'warning'
  | 'rejected'
  | 'upcoming'
  | 'cancelled';

export type PayRunTimelineSubStepState = 'complete' | 'current' | 'rejected' | 'upcoming';

export type PayRunTimelineActionVerb = 'make_payroll_active' | 'request_readiness' | 'submit_approval' | 'finalise' | 'review_banking';

export interface PayRunTimelineSubStep {
  id: string;
  label: string;
  state: PayRunTimelineSubStepState;
  /** Secondary line, e.g. the approver's route role or a leg's item count. */
  detail?: string;
  /** Shown as an avatar at the row's trailing edge. */
  person?: { name: string };
}

/** The stage-specific helper block at the foot of a popover. */
export interface PayRunTimelineFooter {
  title: string;
  detail?: string;
  /** 0-100; renders a progress bar. */
  percent?: number;
}

export interface PayRunTimelineAction {
  verb: PayRunTimelineActionVerb;
  label: string;
}

export interface PayRunTimelineStep {
  id: PayRunTimelineStepId;
  title: string;
  detail: string;
  state: PayRunTimelineStepState;
  menuLabel: string | null;
  subSteps: PayRunTimelineSubStep[];
  footer?: PayRunTimelineFooter | null;
  /**
   * 0-100 through this stage's own work, or null where there is nothing countable to report.
   * Derived from the server's own figures, never from elapsed time.
   */
  progressPercent?: number | null;
  action: PayRunTimelineAction | null;
}

export interface PayRunTimelineModel {
  operatingStateLabel: string;
  operatingStateTone: 'grey' | 'green';
  currentStepId: PayRunTimelineStepId | null;
  steps: PayRunTimelineStep[];
}

export interface PayRunTimelineInput {
  payRun: PayRunDetailDto;
  approval: PayRunApprovalDto | null;
  exceptions: PayRunExceptionsPage | null;
  /** Optional Banking (BSB) evidence. Omitted callers keep the pre-PRR-315 behaviour. */
  banking?: PayRunBankingInput;
}

const WORKFLOW_ORDER = ['open', 'compliance', 'ready_to_pay', 'eft_ready', 'complete'];
const TIMELINE_STAGE_IDS: PayRunTimelineStepId[] = ['pay_period', 'review', 'ready_to_pay', 'approval', 'payment', 'close'];

const submittedDate = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Australia/Sydney',
});

const longDate = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'Australia/Sydney',
});

const milestoneDate = new Intl.DateTimeFormat('en-AU', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'Australia/Sydney',
});

const formatWith = (formatter: Intl.DateTimeFormat, value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : formatter.format(parsed);
};

const formatSubmittedAt = (value: string): string => formatWith(submittedDate, value) ?? value;

const formatApproverRole = (role: string): string =>
  role === 'payroll_manager' ? 'Payroll manager' : role === 'finance_delegate' ? 'Finance delegate' : role;

const liveRequirements = (approval: PayRunApprovalDto | null): PayRunApprovalRequirementDto[] =>
  (approval?.streams ?? [])
    .flatMap((stream) => stream.requirements)
    .filter((requirement) => !requirement.isSuperseded)
    .sort((left, right) => left.approvalOrder - right.approvalOrder);

const issueCount = (exceptions: PayRunExceptionsPage | null): number =>
  (exceptions?.summary.blocking ?? 0)
  + (exceptions?.summary.warnings ?? 0)
  + (exceptions?.summary.reviewRequired ?? 0);

const cancelledStep = (
  id: PayRunTimelineStepId,
  title: string,
  detail: string,
): PayRunTimelineStep => ({
  id, title, detail, state: 'cancelled', menuLabel: null, subSteps: [], action: null,
});

const cancelledTimeline = (_payRun: PayRunDetailDto): PayRunTimelineModel => ({
  operatingStateLabel: 'Cancelled',
  operatingStateTone: 'grey',
  currentStepId: null,
  steps: [
    cancelledStep('pay_period', 'Calculate', 'Cancelled'),
    cancelledStep('review', 'Review', 'Cancelled'),
    cancelledStep('ready_to_pay', 'Submit', 'Cancelled'),
    cancelledStep('approval', 'Approve', 'Cancelled'),
    cancelledStep('payment', 'Close', 'Cancelled'),
    cancelledStep('close', 'Complete', 'Cancelled'),
  ],
});

const calculationTimelineLabel = (payRun: PayRunDetailDto): string => {
  const calculation = payRun.calculation;
  if (!calculation || calculation.status === 'not_ready' || calculation.status === 'ready') return 'Calculation not started';
  if (calculation.status === 'admitted') return 'Calculation queued';
  if (calculation.status === 'calculating') {
    const processed = calculation.succeededWorkerCount
      + calculation.failedWorkerCount
      + calculation.reviewRequiredWorkerCount;
    return `Calculating — ${processed} of ${calculation.workerCount} employees processed`;
  }
  if (calculation.status === 'cancellation_requested') return 'Calculation cancellation requested';
  if (calculation.status === 'complete') return 'Calculation complete';
  if (calculation.status === 'review_required') return 'Calculation needs review';
  if (calculation.status === 'failed') return 'Calculation failed — retry available';
  if (calculation.status === 'cancelled') return 'Calculation cancelled';
  if (calculation.status === 'abandoned') return 'Calculation abandoned';
  return 'Calculation timed out';
};

/** Workers processed as a percentage, or null when the run has no countable population yet. */
export const payRunCalculationPercent = (payRun: PayRunDetailDto): number | null => {
  const calculation = payRun.calculation;
  if (!calculation || !calculation.workerCount) return null;
  const processed = calculation.succeededWorkerCount
    + calculation.failedWorkerCount
    + calculation.reviewRequiredWorkerCount;
  return Math.max(0, Math.min(100, Math.round((processed / calculation.workerCount) * 100)));
};

/** True while the server is still working through the population. */
export const isPayRunCalculationInFlight = (payRun: PayRunDetailDto): boolean =>
  payRun.calculation?.status === 'admitted' || payRun.calculation?.status === 'calculating';

const bankingSubStepState = (banking: PayRunBankingSummary): PayRunTimelineSubStepState => {
  // 'unavailable' is a transient read failure, not a payroll problem, so it must not show a rejected marker on a
  // Pay Run where nothing is actually wrong. Its label already says "unavailable", which is the honest signal.
  if (banking.state === 'unavailable') return 'current';
  if (banking.state === 'blocked') return 'rejected';
  if (banking.state === 'verified') return 'complete';
  if (banking.state === 'attention' || banking.state === 'checking') return 'current';
  return 'upcoming';
};

const buildReviewStep = (
  statusIndex: number,
  exceptions: PayRunExceptionsPage | null,
  banking: PayRunBankingSummary | null,
  paymentDate: string | null,
  eftNotApplicable: boolean,
): PayRunTimelineStep => {
  const past = statusIndex > 1;
  const active = statusIndex === 1;
  const issues = issueCount(exceptions);
  const evaluated = exceptions?.evaluationStatus === 'completed';
  const readinessReason = active ? exceptions?.readinessReason : null;
  // PRR-309 revalidates BSB evidence again at approval and finalisation, so a Banking condition can appear after
  // Review is historically complete. The operator still needs a route back to the Banking panel, so attention is
  // recognised for any Pay Run that has reached Review — not only one still sitting in it.
  // Bounded at 'complete': a finalised Pay Run has already been paid, so surfacing a banking route on it would
  // offer an action that can no longer change anything.
  const reachedReview = active || (past && statusIndex < WORKFLOW_ORDER.indexOf('complete'));
  const bankingAttention = reachedReview && banking !== null && bankingNeedsAttention(banking);

  const complianceDetail = active && issues === 0 && readinessReason
    ? readinessReviewDetail(readinessReason)
    : past || (active && evaluated && issues === 0)
    ? 'No issues'
    : active && issues > 0
      ? `${issues} ${issues === 1 ? 'issue' : 'issues'}`
      : active
        ? 'Compliance'
        : 'Compliance';

  // Banking only replaces the stage detail when it actually needs operator attention; a healthy, checking or
  // not-yet-evaluated Banking state stays low-noise and leaves the normal compliance detail in place.
  const detail = (bankingAttention && banking !== null ? bankingStageDetail(banking) : null) ?? complianceDetail;

  // Lifecycle truth is preserved: a Banking condition found by a later revalidation never reopens a completed
  // Review stage. A past stage stays 'complete' and carries the condition in text and in its action instead.
  const state: PayRunTimelineStepState = past
    ? 'complete'
    : bankingAttention
      ? 'warning'
      : active && (issues > 0 || Boolean(readinessReason))
        ? 'warning'
        : active
          ? 'current'
          : 'upcoming';

  const subSteps: PayRunTimelineSubStep[] = [
    { id: 'compliance_pending', label: 'Pending', state: past ? 'complete' : active ? 'current' : 'upcoming' },
    { id: 'compliance_complete', label: 'Complete', state: past ? 'complete' : 'upcoming' },
  ];
  if (eftNotApplicable)
    subSteps.push({ id: 'banking_bsb', label: 'Banking (BSB): not applicable', state: 'complete' });
  else if (banking !== null)
    subSteps.push({ id: 'banking_bsb', label: bankingSubStepLabel(banking), state: bankingSubStepState(banking) });

  return {
    id: 'review',
    title: 'Review',
    detail,
    state,
    menuLabel: 'REVIEW - COMPLIANCE',
    subSteps,
    footer: paymentDate ? { title: 'Payment date upcoming', detail: paymentDate } : null,
    // While Banking needs attention this action takes precedence, including on a completed Review stage, so the
    // operator always has a route to the findings. Once resolved it falls away and the normal readiness or
    // downstream approval/finalisation action becomes the primary action again.
    action: bankingAttention
      ? { verb: 'review_banking', label: 'Review banking findings' }
      : active && exceptions?.canRequestReadiness
        ? { verb: 'request_readiness', label: 'Run readiness evaluation' }
        : null,
  };
};

const buildReadyToPayStep = (
  statusIndex: number,
  approval: PayRunApprovalDto | null,
): PayRunTimelineStep => {
  const reached = statusIndex >= 2;
  const past = statusIndex > 2;
  const submittedAt = reached && !approval?.isSuperseded ? approval?.submittedAt ?? null : null;
  const rejected = reached && !approval?.isSuperseded
    && liveRequirements(approval).some((requirement) => requirement.status === 'rejected');
  const submitLabel = rejected ? 'Re-submit for approval' : 'Submit for authorisation';

  const detail = !reached
    ? 'Not ready'
    : rejected
      ? 'Re-submit for approval'
      : submittedAt
        ? `Submitted ${formatSubmittedAt(submittedAt)}`
        : 'For authorisation';

  const state: PayRunTimelineStepState = past
    ? 'complete'
    : !reached
      ? 'upcoming'
      : rejected
        ? 'in_progress'
        : submittedAt
          ? 'complete'
          : 'current';

  const submitted = Boolean(submittedAt) && !rejected;

  return {
    id: 'ready_to_pay',
    title: 'Submit',
    detail,
    state,
    menuLabel: 'READY TO PAY',
    subSteps: [
      {
        id: 'submit_for_authorisation',
        label: submitLabel,
        state: past || submitted ? 'complete' : reached ? 'current' : 'upcoming',
      },
      { id: 'submitted', label: 'Submitted', state: past || submitted ? 'complete' : 'upcoming' },
    ],
    footer: submitted && submittedAt
      ? { title: 'Submitted', detail: formatSubmittedAt(submittedAt) }
      : null,
    action: reached && !past && approval?.canSubmit
      ? { verb: 'submit_approval', label: submitLabel }
      : null,
  };
};

const buildApprovalStep = (
  statusIndex: number,
  approval: PayRunApprovalDto | null,
): PayRunTimelineStep => {
  const past = statusIndex > 2;
  const submitted = statusIndex >= 2 && !approval?.isSuperseded && Boolean(approval?.submittedAt);
  // Retained decisions from an earlier cycle cannot advance Approve before a current Submit.
  const streams = (past || submitted ? approval?.streams ?? [] : []).map((stream) => {
    const requirements = stream.requirements.filter((requirement) => !requirement.isSuperseded);
    const requiredApprovalCount = Math.min(stream.requiredApprovalCount, requirements.length);
    const approved = requirements.filter((requirement) => requirement.status === 'approved').length;
    const rejected = requirements.some((requirement) => requirement.status === 'rejected');
    return { ...stream, requiredApprovalCount, requirements, approved, rejected, complete: approved >= requiredApprovalCount };
  });
  const rejected = streams.some((stream) => stream.rejected);
  const allApproved = streams.length > 0 && streams.every((stream) => stream.complete);
  const streamProgress = streams
    .filter((stream) => !stream.complete)
    .map((stream) => `${stream.approvalStream === 'eft_disbursement' ? 'EFT' : 'Super'} ${Math.min(stream.approved, stream.requiredApprovalCount)} of ${stream.requiredApprovalCount}`)
    .join(' · ');

  const detail = rejected
    ? 'Rejected'
    : past || allApproved
      ? 'Final approval'
      : submitted && streamProgress
        ? streamProgress
        : 'Not started';

  const state: PayRunTimelineStepState = rejected
    ? 'rejected'
    : past || allApproved
      ? 'complete'
      : submitted
        ? 'in_progress'
        : 'upcoming';

  // The design lists the people, not the streams: a route carries the same approvers in each
  // stream, so rows are grouped by approver and only read as approved once every stream they sit
  // on is approved.
  const byApprover = new Map<string, PayRunApprovalRequirementDto[]>();
  for (const stream of streams) {
    for (const requirement of stream.requirements) {
      const existing = byApprover.get(requirement.approverUserId);
      if (existing) existing.push(requirement);
      else byApprover.set(requirement.approverUserId, [requirement]);
    }
  }

  let reachedPending = false;
  const approverSubSteps: PayRunTimelineSubStep[] = [...byApprover.entries()]
    .map(([approverUserId, group]) => ({
      approverUserId,
      group,
      order: Math.min(...group.map((requirement) => requirement.approvalOrder)),
    }))
    .sort((left, right) => left.order - right.order)
    .map(({ approverUserId, group }) => {
      const groupRejected = group.some((requirement) => requirement.status === 'rejected');
      const groupApproved = !groupRejected && group.every((requirement) => requirement.status === 'approved');
      const pending = !groupApproved && !groupRejected && submitted && !reachedPending;
      if (pending) reachedPending = true;
      return {
        id: approverUserId,
        label: groupRejected ? 'Changes requested' : groupApproved ? 'Approved' : 'Pending',
        detail: formatApproverRole(group[0].approverRole),
        person: { name: approverUserId },
        state: (groupRejected ? 'rejected' : groupApproved ? 'complete' : pending ? 'current' : 'upcoming') as PayRunTimelineSubStepState,
      };
    });

  const approvalsLeft = approverSubSteps.filter((row) => row.state !== 'complete').length;
  // People decided, out of people in the route: two approvers with one through is half way,
  // which is what the connector draws.
  const approverProgress = approverSubSteps.length > 0
    ? Math.round(((approverSubSteps.length - approvalsLeft) / approverSubSteps.length) * 100)
    : null;

  return {
    id: 'approval',
    title: 'Approve',
    detail,
    state,
    progressPercent: past || allApproved ? 100 : approverProgress,
    menuLabel: 'PAY AUTHORISATION',
    subSteps: approverSubSteps,
    footer: approverSubSteps.length > 0
      ? {
        title: allApproved || past ? 'Payroll approved' : submitted ? 'Payroll pending' : 'Payroll not submitted',
        detail: `${approverSubSteps.length - approvalsLeft}/${approverSubSteps.length} approved`,
      }
      : null,
    action: null,
  };
};

const LEG_LABEL: Record<PayRunFanOutLegDto['legType'], string> = {
  eft: 'EFT payments',
  saff: 'Super (SAFF)',
  stp: 'STP reporting',
  gl: 'General ledger',
  creditor: 'Creditor payments',
  leave: 'Leave balances',
};

const legSubStepState = (leg: PayRunFanOutLegDto): PayRunTimelineSubStepState => {
  if (leg.status === 'succeeded') return 'complete';
  if (leg.status === 'retryable_failure' || leg.status === 'reconciliation_failure') return 'rejected';
  return leg.status === 'processing' ? 'current' : 'upcoming';
};

/** Acknowledged items as a share of expected, across every leg that expects any. */
const legProgressPercent = (legs: PayRunFanOutLegDto[]): number | null => {
  const expected = legs.reduce((total, leg) => total + leg.expectedItemCount, 0);
  if (!expected) return null;
  const acknowledged = legs.reduce((total, leg) => total + (leg.acknowledgedItemCount ?? 0), 0);
  return Math.max(0, Math.min(100, Math.round((acknowledged / expected) * 100)));
};

const buildPaymentStep = (statusIndex: number, payRun: PayRunDetailDto): PayRunTimelineStep => {
  const finalised = Boolean(payRun.finalisation);
  const ready = statusIndex === 3;
  const past = statusIndex > 3 || finalised;

  const due = formatWith(milestoneDate, payRun.eftReleaseDate ?? payRun.paymentDate);
  const legs = payRun.finalisation?.legs ?? [];
  const legsDone = legs.filter((leg) => leg.status === 'succeeded').length;
  const detail = past ? 'Done' : ready ? 'Ready' : due ? `Due ${due}` : 'Not started';
  const state: PayRunTimelineStepState = past ? 'complete' : ready ? 'current' : 'upcoming';

  return {
    id: 'payment',
    title: 'Close',
    detail,
    state,
    progressPercent: legs.length > 0 ? legProgressPercent(legs) : past ? 100 : null,
    menuLabel: 'RELEASE',
    // Before release there is nothing to report but the intent; afterwards the legs are the
    // only honest account of where the money and the filings have got to.
    subSteps: legs.length > 0
      ? legs.map((leg) => ({
        id: leg.legType,
        label: LEG_LABEL[leg.legType] ?? leg.legType,
        detail: leg.expectedItemCount
          ? `${leg.acknowledgedItemCount ?? 0} of ${leg.expectedItemCount} acknowledged`
          : undefined,
        state: legSubStepState(leg),
      }))
      : [
        { id: 'release_pay', label: 'Release pay', state: past ? 'complete' : ready ? 'current' : 'upcoming' },
        { id: 'pay_released', label: 'Pay released', state: past ? 'complete' : 'upcoming' },
      ],
    footer: legs.length > 0
      ? legsDone === legs.length
        ? { title: 'All releases acknowledged', percent: legProgressPercent(legs) ?? undefined }
        : {
          title: `${legsDone} of ${legs.length} releases done`,
          detail: 'Anything that fails is retried and reported here.',
          percent: legProgressPercent(legs) ?? undefined,
        }
      : due ? { title: 'Payment due', detail: due } : null,
    action: ready && payRun.meta.actions.includes('finalise')
      ? { verb: 'finalise', label: 'Release pay' }
      : null,
  };
};

const buildCloseStep = (payRun: PayRunDetailDto): PayRunTimelineStep => {
  const complete = payRun.workflowStatus === 'complete';

  if (payRun.reversedByRunId) {
    return {
      id: 'close', title: 'Complete', detail: 'Reversed', state: 'cancelled',
      menuLabel: null, subSteps: [], action: null,
    };
  }

  const paymentDate = formatWith(submittedDate, payRun.paymentDate);
  const detail = complete ? 'Done' : 'Not started';
  const state: PayRunTimelineStepState = complete ? 'complete' : 'upcoming';

  return {
    id: 'close',
    title: 'Complete',
    detail,
    state,
    menuLabel: 'COMPLETE',
    subSteps: [
      { id: 'pay_run_complete', label: 'Pay run complete', state: complete ? 'complete' : 'upcoming' },
    ],
    footer: complete && paymentDate ? { title: 'Payment date', detail: paymentDate } : null,
    action: null,
  };
};

export const buildPayRunTimeline = ({ payRun, approval, exceptions, banking }: PayRunTimelineInput): PayRunTimelineModel => {
  if (payRun.workflowStatus === 'cancelled') return cancelledTimeline(payRun);

  const statusIndex = WORKFLOW_ORDER.indexOf(payRun.workflowStatus);
  const knownOperatingState = payRun.operatingState === 'trial_pay' || payRun.operatingState === 'payroll_active';
  if (statusIndex < 0 || !knownOperatingState) {
    return {
      operatingStateLabel: 'Status unavailable',
      operatingStateTone: 'grey',
      currentStepId: null,
      steps: [
        ['pay_period', 'Calculate'], ['review', 'Review'], ['ready_to_pay', 'Submit'],
        ['approval', 'Approve'], ['payment', 'Close'], ['close', 'Complete'],
      ].map(([id, title]) => ({
        id: id as PayRunTimelineStepId, title, detail: 'Status unavailable', state: 'upcoming',
        menuLabel: null, subSteps: [], action: null,
      })),
    };
  }

  const steps: PayRunTimelineStep[] = [
    {
      id: 'pay_period',
      title: 'Calculate',
      detail: calculationTimelineLabel(payRun),
      progressPercent: payRunCalculationPercent(payRun),
      // A run mid-calculation is not finished with this step, and saying so is what drives the
      // animated connector. Everything else keeps the prior behaviour.
      state: isPayRunCalculationInFlight(payRun) ? 'in_progress' : 'complete',
      menuLabel: null,
      subSteps: [{
        id: 'calculation',
        label: calculationTimelineLabel(payRun),
        state: payRun.calculation?.status === 'complete' ? 'complete' : 'current',
      }],
      action: null,
    },
    buildReviewStep(
      statusIndex,
      exceptions,
      banking && payRun.eftApplicability !== 'not_applicable' ? summarisePayRunBanking(banking) : null,
      formatWith(longDate, payRun.paymentDate),
      payRun.eftApplicability === 'not_applicable',
    ),
    buildReadyToPayStep(statusIndex, approval),
    buildApprovalStep(statusIndex, approval),
    buildPaymentStep(statusIndex, payRun),
    buildCloseStep(payRun),
  ];

  const lifecycleStageIndex = resolvePayRunLifecycleStageIndex(payRun.workflowStatus, {
    submittedAt: approval?.submittedAt,
    isSuperseded: approval?.isSuperseded ?? false,
    hasRejectedDecision: liveRequirements(approval).some((requirement) => requirement.status === 'rejected'),
  });

  return {
    operatingStateLabel: payRun.operatingState === 'trial_pay' ? 'Trial pay' : 'Active pay',
    operatingStateTone: payRun.operatingState === 'trial_pay' ? 'grey' : 'green',
    currentStepId: TIMELINE_STAGE_IDS[lifecycleStageIndex] ?? null,
    steps,
  };
};

export type PayRunSummaryLeadTone = 'red' | 'yellow' | 'blue' | 'green' | 'neutral';

export interface PayRunSummaryLeadCard {
  id: string;
  label: string;
  value: string | number;
  icon: 'status-critical' | 'status-caution' | 'status-positive' | 'status-no-action' | 'clock';
  tone: PayRunSummaryLeadTone;
}

const LEAD_TONE: Record<PayRunTimelineStepState, PayRunSummaryLeadTone> = {
  complete: 'green',
  current: 'blue',
  in_progress: 'blue',
  warning: 'yellow',
  rejected: 'red',
  upcoming: 'neutral',
  cancelled: 'neutral',
};

const LEAD_ICON: Record<PayRunTimelineStepState, PayRunSummaryLeadCard['icon']> = {
  complete: 'status-positive',
  current: 'status-no-action',
  in_progress: 'clock',
  warning: 'status-caution',
  rejected: 'status-critical',
  upcoming: 'status-no-action',
  cancelled: 'status-no-action',
};

export const buildPayRunSummaryLeadCards = (
  timeline: PayRunTimelineModel,
  exceptions: PayRunExceptionsPage | null,
): PayRunSummaryLeadCard[] => {
  const blocking = exceptions?.summary.blocking ?? 0;
  const warnings = (exceptions?.summary.warnings ?? 0) + (exceptions?.summary.reviewRequired ?? 0);

  const counts: PayRunSummaryLeadCard[] = [];
  if (blocking > 0)
    counts.push({ id: 'critical', label: 'Critical', value: blocking, icon: 'status-critical', tone: 'red' });
  if (warnings > 0)
    counts.push({ id: 'warnings', label: 'Warnings', value: warnings, icon: 'status-caution', tone: 'yellow' });
  if (counts.length > 0) return counts;

  const current = timeline.steps.find((step) => step.state === 'rejected')
    ?? timeline.steps.find((step) => step.id === timeline.currentStepId);
  if (!current) return [];

  return [{
    id: `step-${current.id}`,
    label: current.title,
    value: current.detail,
    icon: LEAD_ICON[current.state],
    tone: LEAD_TONE[current.state],
  }];
};

export const findPayRunPrimaryAction = (timeline: PayRunTimelineModel): PayRunTimelineAction | null =>
  timeline.steps.find((step) => step.action !== null)?.action ?? null;
